/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiIcon } from '../../icon';
import { EuiTab, EuiTabs } from '../../tabs';
import { EuiFlexGroup, EuiFlexItem } from '../../flex';
import { EuiSpacer } from '../../spacer';
import { EuiTitle } from '../../title';
import { EuiText } from '../../text';
import { useIsWithinBreakpoints, useEuiTheme } from '../../../services';
import { EuiScreenReaderOnly } from '../../accessibility';
import { EuiBreadcrumbs } from '../../breadcrumbs';
import { useEuiPaddingCSS } from '../../../global_styling';
import { setStyleForRestrictedPageWidth } from '../_restrict_width';
import { euiPageHeaderStyles } from './page_header.styles';
import { euiPageHeaderContentStyles } from './page_header_content.styles';
export var ALIGN_ITEMS = ['top', 'bottom', 'center', 'stretch'];

// Gets all the tab props including the button or link props

export var EuiPageHeaderContent = ({
  className,
  pageTitle,
  pageTitleProps,
  iconType,
  iconProps,
  tabs,
  tabsProps,
  description,
  breadcrumbs,
  breadcrumbProps,
  alignItems,
  responsive = true,
  rightSideItems,
  rightSideGroupProps,
  children,
  restrictWidth,
  paddingSize: _paddingSize = 'none',
  bottomBorder: _bottom_border,
  style,
  ...rest
}) => {
  const isResponsiveBreakpoint = useIsWithinBreakpoints(['xs', 's'], !!responsive);
  const useTheme = useEuiTheme();
  const classes = classNames('euiPageHeaderContent', className);
  const pageHeaderStyles = euiPageHeaderStyles(useTheme);
  const contentStyles = euiPageHeaderContentStyles(useTheme);
  const styles = setStyleForRestrictedPageWidth(restrictWidth, style);
  let paddingSides = 'vertical';
  let paddingSize = _paddingSize;
  let bottomBorder = _bottom_border;
  const onlyChildren = !tabs && !pageTitle && !rightSideItems && !description && children;
  const onlyTabs = tabs && !pageTitle && !rightSideItems && !description && !children;
  const tabsAsTitle = tabs && !pageTitle;
  const tabsAtTheBottom = pageTitle && tabs;
  const borderWithoutPadding = (!paddingSize || paddingSize === 'none') && bottomBorder;
  if (onlyTabs) {
    paddingSize = 'none';
  } else if (tabsAsTitle) {
    paddingSides = 'bottom';
  } else if (tabsAtTheBottom) {
    paddingSides = 'top';
    bottomBorder = bottomBorder === false ? false : true;
  } else if (borderWithoutPadding) {
    paddingSides = 'bottom';
    paddingSize = 'l';
  }
  const blockPadding = useEuiPaddingCSS(paddingSides);
  const cssStyles = [contentStyles.euiPageHeaderContent, bottomBorder && pageHeaderStyles.border, blockPadding[paddingSize]];
  const childrenOnlyStyles = [contentStyles.flex, contentStyles[alignItems || 'center'], responsive === true && isResponsiveBreakpoint && contentStyles.responsive, responsive === 'reverse' && isResponsiveBreakpoint && contentStyles.responsiveReverse];

  // Don't go any further if there's no other content than children
  if (onlyChildren) {
    return <div css={cssStyles} {...rest}>
        <div css={childrenOnlyStyles}>{children}</div>
      </div>;
  }
  let descriptionNode;
  if (description) {
    descriptionNode = <>
        {(pageTitle || tabs) && <EuiSpacer />}
        <EuiText grow={false}>
          <p>{description}</p>
        </EuiText>
      </>;
  }
  const optionalBreadcrumbs = breadcrumbs ? <>
      <EuiBreadcrumbs breadcrumbs={breadcrumbs} {...breadcrumbProps} />
      <EuiSpacer size="s" />
    </> : undefined;
  let pageTitleNode;
  if (pageTitle) {
    const icon = iconType ? <EuiIcon size="xl" css={contentStyles.euiPageHeaderContent__titleIcon} {...iconProps} type={iconType} /> : undefined;
    pageTitleNode = <EuiTitle {...pageTitleProps} size="l">
        <h1>
          {icon}
          {pageTitle}
        </h1>
      </EuiTitle>;
  }
  let tabsNode;
  if (tabs) {
    const tabsSize = pageTitle ? 'l' : 'xl';
    const renderTabs = () => {
      return tabs.map((tab, index) => {
        const {
          label,
          ...tabRest
        } = tab;
        return <EuiTab key={index} {...tabRest}>
            {label}
          </EuiTab>;
      });
    };

    // When tabs exist without a pageTitle, we need to recreate an h1 based on the currently selected tab and visually hide it
    const screenReaderPageTitle = !pageTitle && <EuiScreenReaderOnly>
        <h1>
          {tabs.find(obj => {
          return obj.isSelected === true;
        })?.label}
        </h1>
      </EuiScreenReaderOnly>;
    tabsNode = <>
        {pageTitleNode && <EuiSpacer />}
        {screenReaderPageTitle}
        <EuiTabs {...tabsProps} bottomBorder={false} size={tabsSize}>
          {renderTabs()}
        </EuiTabs>
      </>;
  }
  const childrenNode = children && <>
      <EuiSpacer />
      {children}
    </>;
  let bottomContentNode;
  if (childrenNode || tabsNode && pageTitleNode) {
    bottomContentNode = <div className="euiPageHeaderContent__bottom">
        {childrenNode}
        {pageTitleNode && tabsNode}
      </div>;
  }

  /**
   * The left side order depends on if a `pageTitle` was supplied.
   * If not, but there are `tabs`, then the tabs become the page title
   */
  let leftSideOrder;
  if (tabsNode && !pageTitleNode) {
    leftSideOrder = <>
        {tabsNode}
        {descriptionNode}
      </>;
  } else {
    leftSideOrder = <>
        {pageTitleNode}
        {descriptionNode}
      </>;
  }
  let rightSideFlexItem;
  if (rightSideItems && rightSideItems.length) {
    const itemsToRender = isResponsiveBreakpoint ? rightSideItems : [...rightSideItems].reverse();
    const rightSideFlexItems = itemsToRender.map((item, index) => <EuiFlexItem grow={false} key={index}>
        {item}
      </EuiFlexItem>);
    rightSideFlexItem = <EuiFlexItem grow={false}>
        <EuiFlexGroup wrap responsive={false} {...rightSideGroupProps}>
          {rightSideFlexItems}
        </EuiFlexGroup>
      </EuiFlexItem>;
  }
  return alignItems === 'top' || isResponsiveBreakpoint ? <div className={classes} css={cssStyles} style={styles} {...rest}>
      {optionalBreadcrumbs}
      <EuiFlexGroup responsive={!!responsive} className="euiPageHeaderContent__top" alignItems={pageTitle ? 'flexStart' : 'baseline'} gutterSize="l">
        {isResponsiveBreakpoint && responsive === 'reverse' ? <>
            {rightSideFlexItem}
            <EuiFlexItem>{leftSideOrder}</EuiFlexItem>
          </> : <>
            <EuiFlexItem>{leftSideOrder}</EuiFlexItem>
            {rightSideFlexItem}
          </>}
      </EuiFlexGroup>
      {bottomContentNode}
    </div> : <div className={classes} css={cssStyles} style={styles} {...rest}>
      {optionalBreadcrumbs}
      <EuiFlexGroup responsive={!!responsive} className="euiPageHeaderContent__top" alignItems={alignItems === 'bottom' ? 'flexEnd' : alignItems} gutterSize="l">
        <EuiFlexItem>
          {leftSideOrder}
          {bottomContentNode}
        </EuiFlexItem>
        {rightSideFlexItem}
      </EuiFlexGroup>
    </div>;
};
EuiPageHeaderContent.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
     * The only option is on/off
     */
  bottomBorder: PropTypes.bool,
  /**
     * Adjust the padding.
     * When using this setting it's best to be consistent throughout all similar usages
     */
  paddingSize: PropTypes.any,
  /**
     * Set to false if you don't want the children to stack at small screen sizes.
     * Set to `reverse` to display the right side content first for the sake of hierarchy (like global time)
     */
  responsive: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.oneOf(["reverse"])]),
  /**
     * Vertical alignment of the left and right side content;
     * Default is `center` for custom content, but `top` for when `pageTitle` or `tabs` are included
     */
  alignItems: PropTypes.any,
  /**
     * Pass custom an array of content to this side usually up to 3 buttons.
     * The first button should be primary, usually with `fill` and will be visually displayed as the last item,
     * but first in the tab order
     */
  rightSideItems: PropTypes.arrayOf(PropTypes.node.isRequired),
  /**
     * Additional EuiFlexGroup props to pass to the container of the `rightSideItems`
     */
  rightSideGroupProps: PropTypes.any,
  /**
     * Custom children will be rendered before the `tabs` unless no `pageTitle` is present, then it will be the last item
     */
  children: PropTypes.node,
  /**
     * Position is dependent on existing with a `pageTitle` or `tabs`
     * Automatically get wrapped in a single paragraph tag inside an EuiText block
     */
  description: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.node.isRequired]),
  /**
     * Wrapped in an `H1` so choose appropriately.
     * A simple string is best
     */
  pageTitle: PropTypes.node,
  /**
     * Additional props to pass to the EuiTitle
     */
  pageTitleProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    textTransform: PropTypes.any,
    id: PropTypes.string
  }),
  /**
     * Optional icon to place to the left of the title
     */
  iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "error", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelionApp", "timeRefresh", "timeslider", "training", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenDenseVector", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]),
  /**
     * Additional EuiIcon props to apply to the optional icon
     */
  iconProps: PropTypes.any,
  /**
     * Optional array breadcrumbs that render before the `pageTitle`
     */
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    href: PropTypes.string,
    rel: PropTypes.string,
    onClick: PropTypes.func,
    /**
         * Visible label of the breadcrumb
         */
    text: PropTypes.node.isRequired,
    /**
         * Force a max-width on the breadcrumb text
         */
    truncate: PropTypes.bool,
    /**
         * Accepts any EuiLink `color` when rendered as one (has `href` or `onClick`)
         */
    color: PropTypes.any,
    /**
         * Override the existing `aria-current` which defaults to `page` for the last breadcrumb
         */
    "aria-current": PropTypes.any
  }).isRequired),
  /**
     * Adjust the props of [EuiBreadcrumbs](#/navigation/breadcrumbs)
     */
  breadcrumbProps: PropTypes.any,
  /**
     * In-app navigation presented as large borderless tabs.
     * Accepts an array of `EuiTab` objects;
     */
  tabs: PropTypes.arrayOf(PropTypes.shape({
    /**
       * Visible text of the tab
       */
    label: PropTypes.node.isRequired
  }).isRequired),
  /**
     * Any extras to apply to the outer tabs container.
     * Extends `EuiTabs`
     */
  tabsProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    /**
         * ReactNode to render as this component's content
         */
    children: PropTypes.node,
    /**
         * Adds a bottom border to separate it from the content after
         */
    bottomBorder: PropTypes.bool
  }),
  /**
     * Sets the max-width of the page,
     * set to `true` to use the default size of `1200px`,
     * set to `false` to not restrict the width,
     * set to a number for a custom width in px,
     * set to a string for a custom width in custom measurement.
     */
  restrictWidth: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.number.isRequired, PropTypes.string.isRequired])
};
try {
  EuiPageHeaderContent.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/page/page_header/page_header_content.tsx',
    description: '',
    displayName: 'EuiPageHeaderContent',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      bottomBorder: {
        defaultValue: null,
        description: 'The only option is on/off',
        name: 'bottomBorder',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      paddingSize: {
        defaultValue: null,
        description: 'Adjust the padding.\n' + "When using this setting it's best to be consistent throughout all similar usages",
        name: 'paddingSize',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "none" | "m" | "xs" | "l" | "xl"',
          value: [{
            value: '"s"'
          }, {
            value: '"none"'
          }, {
            value: '"m"'
          }, {
            value: '"xs"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }]
        }
      },
      responsive: {
        defaultValue: {
          value: 'true'
        },
        description: "Set to false if you don't want the children to stack at small screen sizes.\n" + 'Set to `reverse` to display the right side content first for the sake of hierarchy (like global time)',
        name: 'responsive',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        }],
        required: false,
        type: {
          name: 'boolean | "reverse"'
        }
      },
      alignItems: {
        defaultValue: null,
        description: 'Vertical alignment of the left and right side content;\n' + 'Default is `center` for custom content, but `top` for when `pageTitle` or `tabs` are included',
        name: 'alignItems',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"center" | "top" | "bottom" | "stretch"',
          value: [{
            value: '"center"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }, {
            value: '"stretch"'
          }]
        }
      },
      rightSideItems: {
        defaultValue: null,
        description: 'Pass custom an array of content to this side usually up to 3 buttons.\n' + 'The first button should be primary, usually with `fill` and will be visually displayed as the last item,\n' + 'but first in the tab order',
        name: 'rightSideItems',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        }],
        required: false,
        type: {
          name: 'ReactNode[]'
        }
      },
      rightSideGroupProps: {
        defaultValue: null,
        description: 'Additional EuiFlexGroup props to pass to the container of the `rightSideItems`',
        name: 'rightSideGroupProps',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: '_EuiPageHeaderContentProps'
        }],
        required: false,
        type: {
          name: 'Partial<EuiFlexGroupProps>'
        }
      },
      description: {
        defaultValue: null,
        description: 'Position is dependent on existing with a `pageTitle` or `tabs`\n' + 'Automatically get wrapped in a single paragraph tag inside an EuiText block',
        name: 'description',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentLeft'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentLeft'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      pageTitle: {
        defaultValue: null,
        description: 'Wrapped in an `H1` so choose appropriately.\nA simple string is best',
        name: 'pageTitle',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      pageTitleProps: {
        defaultValue: null,
        description: 'Additional props to pass to the EuiTitle',
        name: 'pageTitleProps',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'Omit<EuiTitleProps, "children" | "size">'
        }
      },
      iconType: {
        defaultValue: null,
        description: 'Optional icon to place to the left of the title',
        name: 'iconType',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'IconType'
        }
      },
      iconProps: {
        defaultValue: null,
        description: 'Additional EuiIcon props to apply to the optional icon',
        name: 'iconProps',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'Partial<Omit<EuiIconProps, "type">>'
        }
      },
      breadcrumbs: {
        defaultValue: null,
        description: 'Optional array breadcrumbs that render before the `pageTitle`',
        name: 'breadcrumbs',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'EuiBreadcrumbProps[]'
        }
      },
      breadcrumbProps: {
        defaultValue: null,
        description: 'Adjust the props of [EuiBreadcrumbs](#/navigation/breadcrumbs)',
        name: 'breadcrumbProps',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTitle'
        }],
        required: false,
        type: {
          name: 'Partial<Omit<EuiBreadcrumbsProps, "breadcrumbs">>'
        }
      },
      tabs: {
        defaultValue: null,
        description: 'In-app navigation presented as large borderless tabs.\n' + 'Accepts an array of `EuiTab` objects;',
        name: 'tabs',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTabs'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTabs'
        }],
        required: false,
        type: {
          name: 'Tab[]'
        }
      },
      tabsProps: {
        defaultValue: null,
        description: 'Any extras to apply to the outer tabs container.\nExtends `EuiTabs`',
        name: 'tabsProps',
        parent: {
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTabs'
        },
        declarations: [{
          fileName: 'app/src/components/page/page_header/page_header_content.tsx',
          name: 'EuiPageHeaderContentTabs'
        }],
        required: false,
        type: {
          name: 'Omit<EuiTabsProps, "size" | "expand">'
        }
      },
      restrictWidth: {
        defaultValue: null,
        description: 'Sets the max-width of the page,\n' + 'set to `true` to use the default size of `1200px`,\n' + 'set to `false` to not restrict the width,\n' + 'set to a number for a custom width in px,\n' + 'set to a string for a custom width in custom measurement.',
        name: 'restrictWidth',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/_restrict_width.ts',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string | number | boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', '_EuiPageHeaderContentProps', 'EuiPageHeaderContentLeft', 'EuiPageHeaderContentTitle', 'EuiPageHeaderContentTabs']
  };
} catch (e) {}